'use client';

/* ──────────────────────────────────────────────
 *  BookingCodeField — champ unique « Code promo ou carte
 *  cadeau » du récap : une pastille par code posé (un code
 *  promo et une carte cadeau au plus), chacune retirable, et
 *  la saisie reste disponible pour le second code. Le serveur
 *  tranche la nature du code (cf. BookingCodesContext). Un
 *  code apporté par le lien ou la visite et pas encore accepté
 *  reste dans le champ, avec son motif, jusqu'à être accepté
 *  ou effacé par le client. Le QR d'une carte cadeau ou d'un
 *  lien promo se scanne à la caméra : le code lu est posé
 *  aussitôt.
 * ────────────────────────────────────────────── */

import { type JSX, useCallback, useEffect, useId, useRef, useState } from 'react';
import { LuScanQrCode } from 'react-icons/lu';
import { isCameraScanAvailable } from '../../../shared/useBarcodeScanner';
import { useBooking } from '../../context/BookingContext';
import { useBookingCodes } from '../../context/BookingCodesContext';
import type { AppliedCodes } from '../../types';
import { sameCode } from '../../utils/same-code';
import { VoucherScanner } from './VoucherScanner';

const CODE_KINDS: ReadonlyArray<keyof AppliedCodes> = ['promo', 'voucher'];
const KIND_ICON: Record<keyof AppliedCodes, string> = { promo: '🏷', voucher: '🎁' };

export function BookingCodeField(): JSX.Element {
  const { labels } = useBooking();
  const { codes, applyCode, removeCode, busy, locked, feedback, pendingCode, dismissPendingCode } = useBookingCodes();
  const [value, setValue] = useState('');
  const inputId = useId();
  const scannerId = useId();
  const disabled = busy || locked;
  // Lu après le montage : `window` est absent au rendu serveur du tunnel.
  const [canScan, setCanScan] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  useEffect((): void => {
    setCanScan(isCameraScanAvailable());
  }, []);

  // Champ figé (pré-réservation, paiement) : la caméra se coupe et ne se rouvre pas d'elle-même.
  useEffect((): void => {
    if (locked) setIsScanning(false);
  }, [locked]);

  /* Le code en attente occupe le champ tant que le client n'y écrit rien d'autre ; accepté ou retiré, il
     libère le champ s'il y est encore. */
  const shownPendingRef = useRef<string | undefined>(undefined);
  useEffect((): void => {
    const previous = shownPendingRef.current;
    if (pendingCode === previous) return;
    shownPendingRef.current = pendingCode;
    setValue((current): string => {
      if (pendingCode) return current === '' || sameCode(current, previous) ? pendingCode : current;
      return sameCode(current, previous) ? '' : current;
    });
  }, [pendingCode]);

  const submit = useCallback(async (): Promise<void> => {
    if (disabled) return;
    // Posé : le champ se libère, ou reprend le code encore en attente derrière.
    if (await applyCode(value)) setValue(pendingCode && !sameCode(value, pendingCode) ? pendingCode : '');
  }, [disabled, applyCode, value, pendingCode]);

  const onChange = useCallback(
    (next: string): void => {
      setValue(next);
      // Le client efface ou remplace le code en attente : il n'est plus réessayé.
      if (pendingCode && !sameCode(next, pendingCode)) dismissPendingCode(pendingCode);
    },
    [pendingCode, dismissPendingCode],
  );

  /* Le scan est un geste explicite du client : le code lu remplace la saisie et part aussitôt (le code d'un lien,
     lui, attend « Appliquer »). Refusé, il reste dans le champ avec son motif. */
  const applyScannedCode = useCallback(
    async (scanned: string): Promise<void> => {
      setIsScanning(false);
      onChange(scanned);
      if (disabled) return;
      if (await applyCode(scanned)) setValue(pendingCode && !sameCode(scanned, pendingCode) ? pendingCode : '');
    },
    [onChange, disabled, applyCode, pendingCode],
  );

  const kindLabel = (kind: keyof AppliedCodes): string =>
    kind === 'promo' ? labels.code_kind_promo : labels.code_kind_voucher;

  return (
    <div className="bt-code-field">
      <label className="bt-code-field__label" htmlFor={inputId}>
        {labels.voucher_label}
      </label>

      {(codes.promo || codes.voucher) && (
        <div className="bt-code-field__chips">
          {CODE_KINDS.map((kind): JSX.Element | null => {
            const code = codes[kind];
            if (!code) return null;
            return (
              <span key={kind} className="bt-code-chip" title={kindLabel(kind)}>
                <span className="bt-code-chip__icon" aria-hidden="true">
                  {KIND_ICON[kind]}
                </span>
                <span className="bt-code-chip__code">{code}</span>
                <button
                  type="button"
                  className="bt-code-chip__remove"
                  onClick={(): void => {
                    void removeCode(kind);
                  }}
                  disabled={disabled}
                  aria-label={labels.code_remove.replace('{code}', code)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="bt-voucher__row">
        <input
          id={inputId}
          type="text"
          className="bt-field__input"
          value={value}
          onChange={(e): void => onChange(e.target.value)}
          onKeyDown={(e): void => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            void submit();
          }}
          placeholder={labels.voucher_placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {canScan && (
          <button
            type="button"
            className="bt-voucher__scan"
            onClick={(): void => setIsScanning((open): boolean => !open)}
            disabled={disabled}
            aria-label={labels.voucher_scan_open}
            title={labels.voucher_scan_open}
            aria-expanded={isScanning}
            aria-controls={isScanning ? scannerId : undefined}
          >
            <LuScanQrCode aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="bt-cta bt-cta--small"
          onClick={(): void => {
            void submit();
          }}
          disabled={!value.trim() || disabled}
        >
          {busy ? <span className="bt-spinner bt-spinner--inline" /> : labels.voucher_apply}
        </button>
      </div>

      {isScanning && (
        <VoucherScanner
          id={scannerId}
          onCode={(scanned): void => {
            void applyScannedCode(scanned);
          }}
          onClose={(): void => setIsScanning(false)}
          labels={labels}
        />
      )}

      {feedback && (
        <span className={`bt-voucher__feedback bt-voucher__feedback--${feedback.type}`} role="status">
          {feedback.message}
        </span>
      )}
    </div>
  );
}
